import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import secureAxios from "../../utils/secureAxios";
import { useAuth } from "../../contexts/AuthContext";
import { getApiBaseURL } from "../../utils/api";
import dayjs from "dayjs";

const EditTicketForm = () => {
  const { ticket_id } = useParams();
  const navigate = useNavigate();
  const { tenant_id, user } = useAuth();

  const [ticket, setTicket] = useState(null);
  const [users, setUsers] = useState([]);
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("normal");
  const [assignedTo, setAssignedTo] = useState("");
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");

  const apiBase = getApiBaseURL();
  // const apiBase = process.env.REACT_APP_API_BASE || "http://localhost:5000";

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ticketRes, usersRes, commentsRes] = await Promise.all([
          secureAxios.get(`${apiBase}/api/tickets/${ticket_id}`),
          secureAxios.get(`${apiBase}/api/users?tenant_id=${tenant_id}`),
          secureAxios.get(`${apiBase}/api/tickets/${ticket_id}/comments`),
        ]);

        const t = ticketRes.data;
        setTicket(t);
        setStatus(t.status);
        setPriority(t.priority);
        setAssignedTo(t.assigned_to || "");
        setUsers(usersRes.data);
        setComments(commentsRes.data.reverse());
      } catch (err) {
        console.error("❌ Error loading ticket data:", err);
      }
    };

    fetchData();
  }, [ticket_id, tenant_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await secureAxios.put(`${apiBase}/api/tickets/${ticket_id}`, {
        status,
        priority,
        assigned_to: assignedTo || null,
      });
      navigate("/crm/tickets");
    } catch (err) {
      console.error("❌ Error updating ticket:", err);
      alert("Failed to update ticket");
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    try {
      await secureAxios.post(`${apiBase}/api/tickets/${ticket_id}/comments`, {
        user_id: user?.user_id,
        name: user?.name,
        comment_text: newComment,
      });
      setNewComment("");
      const updated = await secureAxios.get(
        `${apiBase}/api/tickets/${ticket_id}/comments`
      );
      setComments(updated.data.reverse());
    } catch (err) {
      console.error("Error posting comment", err);
    }
  };

  const getAttachmentType = (filename) => {
    if (!filename || typeof filename !== "string") return "other";
    const ext = filename.toLowerCase().split(".").pop();
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return "image";
    if (ext === "pdf") return "pdf";
    if (["doc", "docx"].includes(ext)) return "word";
    if (["xls", "xlsx"].includes(ext)) return "excel";
    return "other";
  };

  if (!ticket) return <div className="p-3">Loading ticket...</div>;

  return (
    <div className="container mt-3">
      <div className="card p-4">
        <h4 className="mb-3">Edit Ticket</h4>

        <div className="mb-3">
          <strong>Subject:</strong> {ticket.subject}
        </div>
        <div className="mb-3">
          <strong>Description:</strong>
          <p className="border p-2 bg-light rounded">{ticket.description}</p>
        </div>

        {ticket.attachment_path && (
          <div className="mb-3">
            <strong>Attachment:</strong>
            <div className="mt-2">
              {getAttachmentType(ticket.attachment_name) === "image" ? (
                <img
                  src={`${apiBase}/${ticket.attachment_path}`}
                  alt="Attachment Preview"
                  className="img-fluid rounded border"
                  style={{ maxHeight: "300px" }}
                />
              ) : getAttachmentType(ticket.attachment_name) === "pdf" ? (
                <a
                  href={`${apiBase}/${ticket.attachment_path}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-danger"
                >
                  📄 View PDF
                </a>
              ) : getAttachmentType(ticket.attachment_name) === "word" ? (
                <a
                  href={`${apiBase}/${ticket.attachment_path}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-primary"
                >
                  📝 View Word Doc
                </a>
              ) : getAttachmentType(ticket.attachment_name) === "excel" ? (
                <a
                  href={`${apiBase}/${ticket.attachment_path}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-success"
                >
                  📊 View Excel Sheet
                </a>
              ) : (
                <a
                  href={`${apiBase}/${ticket.attachment_path}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-secondary"
                >
                  📎 Download File
                </a>
              )}
            </div>
            <div className="text-muted small mt-1">
              {ticket.attachment_name}
            </div>
          </div>
        )}

        <div className="mb-3">
          <strong>Created by:</strong> {ticket.created_by} |{" "}
          <strong>Tenant:</strong> {ticket.tenant_name}
        </div>

        <form onSubmit={handleSubmit} className="mb-4">
          <div className="mb-2">
            <label className="form-label">Status</label>
            <select
              className="form-select"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">Select status...</option>
              <option value="open">Open</option>
              <option value="in progress">In Progress</option>
              <option value="closed">Closed</option>
            </select>
          </div>

          <div className="mb-2">
            <label className="form-label">Priority</label>
            <select
              className="form-select"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            >
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>

          <div className="mb-3">
            <label className="form-label">Assign To</label>
            <select
              className="form-select"
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
            >
              <option value="">Unassigned</option>
              {users.map((user) => (
                <option key={user.user_id} value={user.user_id}>
                  {user.name}
                </option>
              ))}
            </select>
          </div>

          <div className="d-flex justify-content-between">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate("/crm/tickets")}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-success">
              Save Changes
            </button>
          </div>
        </form>

        <hr />

        <h5 className="mb-3">Comments</h5>
        <ul className="list-group mb-3">
          {comments.map((c) => (
            <li key={c.comment_id} className="list-group-item">
              <div>
                <strong>{c.name}</strong>{" "}
                <span className="text-muted small">
                  ({dayjs(c.created_at).format("MMM D, YYYY h:mm A")})
                </span>
              </div>
              <div>{c.comment_text}</div>
            </li>
          ))}
        </ul>

        <form onSubmit={handleCommentSubmit}>
          <textarea
            className="form-control mb-2"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add a comment..."
            required
          />
          <button className="btn btn-outline-primary">Add Comment</button>
        </form>
      </div>
    </div>
  );
};

export default EditTicketForm;
